'use client';

import { DialogBase } from '../../../DialogBase/DialogBase';
import {
  TwoFaModalState,
  useTwoFaModalState,
} from './utils/useTwoFaModalState';
import { TwoFaLayoutWrapper } from '../shared/TwoFaLayoutWrapper';
import { InitialPage } from './subPages/InitialPage';
import { ProgressIndicatorWithSteps } from '../../../ProgressIndicatorWithSteps/ProgressIndicatorWithSteps';
import { ScanQrPage } from './subPages/ScanQrPage';
import { InputCodePage } from '../shared/InputCodePage/InputCodePage';
import { RecoveryCodesPage } from './subPages/RecoveryCodesPage/RecoveryCodesPage';
import { FinishedSummaryPage } from './subPages/FinishedSummaryPage/FinishedSummaryPage';
import { useCallback, useState } from 'react';
import { dismissTwoFaSetup } from '../../../../../auth';
import { useUserInfoContext } from '../../../../userinfo/UserInfoContext/UserInfoProvider';
import { ModalComponent } from '../../../ModalDrawerContext/ModalDrawerContext';
import { useHandleGqlErrorsWithSnackbar } from '../../../../utils/errors';
import { TwoFaScreenTranslations } from '../ResetTwoFaModal/ResetTwoFaModal';

const SetupTwoFaModal: ModalComponent<{
  translations: TwoFaScreenTranslations;
}> = ({ onClose, translations }) => {
  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const { refreshUserInfo } = useUserInfoContext();

  const [dismissLoading, setDismissLoading] = useState(false);

  const {
    currentStep,
    onContinue,
    currentStepNumber,
    recoveryCodes,
    setRecoveryCodes,
    setCurrentStep,
  } = useTwoFaModalState();

  const isCloseDisabledInTheCorner =
    currentStep === TwoFaModalState.INITIAL_PAGE ||
    currentStep === TwoFaModalState.RECOVERY_CODES_PAGE ||
    currentStep === TwoFaModalState.SUMMARY_PAGE;

  const onDismiss = async () => {
    setDismissLoading(true);

    if (!recoveryCodes) {
      const respDismissTwoFa = await dismissTwoFaSetup();

      if (respDismissTwoFa?.errors) {
        processErrors(respDismissTwoFa.errors);
      }
    }

    await refreshUserInfo();

    setDismissLoading(false);

    onClose();
  };

  const backToPreviousStep = useCallback(() => {
    switch (currentStep) {
      case TwoFaModalState.INPUT_CODE_PAGE:
        return () => setCurrentStep(TwoFaModalState.SCAN_QR_PAGE);

      case TwoFaModalState.SUMMARY_PAGE:
        return () => setCurrentStep(TwoFaModalState.RECOVERY_CODES_PAGE);

      default:
        return undefined;
    }
  }, [currentStep, setCurrentStep]);

  return (
    <DialogBase
      backAction={backToPreviousStep()}
      id="2fa-modal"
      onClose={onDismiss}
      size="medium"
      visual="secondary"
      title={translations.welcome2faTitle}
      onMobileFullScreen
      contentProps={{
        disableContentPadding: true,
      }}
      disableCloseButton={isCloseDisabledInTheCorner}
    >
      <ProgressIndicatorWithSteps
        currentStep={currentStepNumber}
        totalSteps={5}
      />
      <TwoFaLayoutWrapper>
        {currentStep === TwoFaModalState.INITIAL_PAGE && (
          <InitialPage
            onDismiss={onDismiss}
            onContinue={onContinue}
            dismissLoading={dismissLoading}
            translations={translations}
          />
        )}
        {currentStep === TwoFaModalState.SCAN_QR_PAGE && (
          <ScanQrPage onContinue={onContinue} translations={translations} />
        )}
        {currentStep === TwoFaModalState.INPUT_CODE_PAGE && (
          <InputCodePage
            onContinue={onContinue}
            bindRecoveryCodes={(newCode: string) => setRecoveryCodes(newCode)}
            actionVariant="setup"
            translations={translations}
          />
        )}
        {currentStep === TwoFaModalState.RECOVERY_CODES_PAGE && (
          <RecoveryCodesPage
            onContinue={onContinue}
            recoveryCodes={recoveryCodes}
            translations={translations}
          />
        )}
        {currentStep === TwoFaModalState.SUMMARY_PAGE && (
          <FinishedSummaryPage />
        )}
      </TwoFaLayoutWrapper>
    </DialogBase>
  );
};

export { SetupTwoFaModal };
